import React, { ChangeEvent, useEffect } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";

// Type definitions for CompactSelect
interface OptionType {
  value: string;
  label: string;
}

interface CompactSelectProps {
  label: string;
  options: OptionType[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  sx?: object;
  required?: boolean;
  name?: string;
  error?: boolean;
  helperText?: string;
  validateOnBlur?: (value: string) => string | null;
  setFunction?: (value: any) => void;
  slotProps?: {
    inputLabel?: {
      shrink?: boolean;
    };
    htmlInput?: {
      [key: string]: any;
    };
  };
 onFocus?: () => void;
}

// Type definitions for CompactTextField
interface CompactTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  disabled?: boolean;
  sx?: object;
  slotProps?: {
    inputLabel?: {
      shrink?: boolean;
    };
    htmlInput?: {
      [key: string]: any;
    };
  };
  isPricingFrom?: boolean;
  required?: boolean;
  name?: string;
  error?: boolean;
  helperText?: string;
  validateOnBlur?: (value: string) => string | null;
  className?: string;
  onFocus?: () => void;
}

// CompactSelect Component
const CompactSelect: React.FC<CompactSelectProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  sx,
  required = false,
  name,
  error = false,
  helperText = "",
  validateOnBlur,
  setFunction,
  slotProps,
  onFocus,
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    const selectedOption: any = options.find(
      (opt) => opt.value === selectedValue
    );
    if (label === "Contract Type" && selectedOption && setFunction) {
      setFunction(selectedOption.name || selectedOption.label || "");
    }

    onChange(selectedValue);
  };

const handleBlur = () => {
    if (validateOnBlur && name) {
      validateOnBlur(value); // Always validate on blur
    }
  }
const handleFocus = () => {
    // Mark the field as touched when focused
    if (name && validateOnBlur) {
      validateOnBlur(value); // Optional: Validate on focus to ensure touched state is updated
    }
  };
  const selectColor =
    value === "buy" ? "green" : value === "sell" ? "red" : "inherit";
  const sortedOptions = [...options].sort((a, b) =>
    a.label?.localeCompare(b.label)
  );

  // Dynamic label props based on whether a value is selected
  const dynamicLabelProps = value
    ? { shrink: true, ...slotProps?.inputLabel }
    : slotProps?.inputLabel || {};

  return (
    <FormControl
      fullWidth
      size="small"
      error={error}
      sx={{
        ...sx,
        flex: {
          xs: "1 1 auto",
          sm: "1 1 25%",
          md: "1 1 25%",
          lg: "1 1 25%",
          xl: "1 1 12%",
        },
        width: { sm: "25%", md: "25%", lg: "25%", xl: "12%", xs: "100%" },
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: "32px",
          fontWeight: "bold",
          marginBottom: "0.5rem",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.9rem",
          transform: "translate(14px, 8px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -8px) scale(0.74)",
            paddingRight: "0.4rem",
            fontWeight: "bold",
            color: "black",
            letterSpacing: "normal",
          },
        },
        "& .MuiInputBase-input": {
          // textTransform: "capitalize",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#b9baeb85",
          borderWidth: "2px",
        },
      }}
      disabled={disabled}
    >
      <InputLabel {...dynamicLabelProps}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={handleChange}
        onBlur={() => validateOnBlur?.(value)}
        onFocus={onFocus}
        variant="outlined"
        displayEmpty
        sx={{
          "& .MuiSelect-select": {
            py: 0.5,
            color: value ? selectColor : "#999",
          },
        }}
      >
        {!value && (
          <MenuItem
            value=""
            disabled
            sx={{ fontSize: "0.7rem", color: "#999" }}
          >
            Select
          </MenuItem>
        )}
        {sortedOptions.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ fontSize: "0.7rem", textTransform: "capitalize" }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ fontSize: "0.6rem", marginLeft: 1 }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// CompactTextField Component
const CompactTextField: React.FC<CompactTextFieldProps> = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  sx,
  slotProps,
  isPricingFrom = false,
  required = false,
  name,
  error = false,
  helperText = "",
  validateOnBlur,
  className,
  onFocus,
}) => {
  useEffect(() => {
    if (type === "date" && isPricingFrom && value) {
      const dateParts = value.split("-");
      if (dateParts.length === 3 && dateParts[2] !== "01") {
        setTimeout(() => {
          onChange(`${dateParts[0]}-${dateParts[1]}-01`);
        }, 0);
      }
    }
  }, [value, isPricingFrom, type, onChange]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;

    if (type === "number") {
      // Allow digits and optionally a decimal point; block invalid inputs
      if (/^\d*\.?\d*$/.test(newValue) || newValue === "") {
        onChange(newValue);
      }
      return; // Exit early for number type
    }

    if (type === "date" && isPricingFrom && newValue) {
      const dateParts = newValue.split("-");
      if (dateParts.length === 3) {
        newValue = `${dateParts[0]}-${dateParts[1]}-01`;
      }
    }

    onChange(newValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number" && (event.key === "e" || event.key === "E")) {
      event.preventDefault();
    }
  };

  const handleBlur = () => {
    if (type === "date" && isPricingFrom && value) {
      const dateParts = value.split("-");
      if (dateParts.length === 3 && dateParts[2] !== "01") {
        onChange(`${dateParts[0]}-${dateParts[1]}-01`);
      }
    }
    if (validateOnBlur && name) {
      validateOnBlur(value); // Always validate on blur
    }
  };

  const handleFocus = () => {
    // Mark the field as touched when focused
    if (name && validateOnBlur) {
      validateOnBlur(value); // Optional: Validate on focus to ensure touched state is updated
    }
    if (onFocus) {
      onFocus();
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown} // Add keydown handler
      onBlur={handleBlur}
      onFocus={handleFocus}
      variant="outlined"
      type={type}
      disabled={disabled}
      error={error}
      helperText={helperText}
      className={className}
      InputLabelProps={{
        shrink: type === "date" ? true : slotProps?.inputLabel?.shrink,
      }}
      InputProps={{
        sx: {
          fontSize: "0.7rem",
          height: "32px",
        },
        ...slotProps?.htmlInput,
      }}
      sx={{
        ...sx,
        flex: {
          xs: "1 1 auto",
          sm: "1 1 25%",
          md: "1 1 25%",
          lg: "1 1 25%",
          xl: "1 1 12%",
        },
        width: { sm: "25%", md: "25%", lg: "25%", xl: "12%", xs: "100%" },
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: "32px",
          fontWeight: "bold",
          marginBottom: "0.5rem",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.9rem",
          transform: "translate(14px, 8px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -8px) scale(0.74)",
            paddingRight: "0.4rem",
            fontWeight: "bold",
            color: "black",
            letterSpacing: "normal",
          },
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#b9baeb85",
          borderWidth: "2px",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#999",
          opacity: 1,
        },
        "& .MuiInputBase-input": {
          // textTransform: "capitalize",
        },
        "& .MuiInputBase-input[type='date']": {
          color: value ? "inherit" : "#999",
          "&::-webkit-datetime-edit": {
            color: value ? "inherit" : "#999",
          },
          "&::-webkit-datetime-edit-fields-wrapper": {
            color: value ? "inherit" : "#999",
          },
        },
      }}
    />
  );
};

export { CompactSelect, CompactTextField };